/**
 * @typedef {Object} MetaService
 *
 * @property {string} title
 * @property {string} description
 * @property {string} keywords
 *
 * @property {function} setTitleToDefault
 * @property {function} updateTitleStructure
 * @property {function} setDescriptionToDefault
 * @property {function} updateDescriptionStructure
 * @property {function} setDescriptionTemplate
 * @property {function} setTitleTemplate
 * @property {function} resetToDefaults
 * @property {function} disableIndexing
 * @property {function} addPrerenderHttpStatus
 */

/**
 * @typedef {Object} TitleStructure
 *
 * @property {string} site
 * @property {string} page
 * @property {string} subpage
 * @property {string} suffix
 */

/**
 * @typedef {Object} DescriptionStructure
 *
 * @property {string} site
 * @property {string} page
 * @property {string} subpage
 * @property {string} suffix
 */



(function() {
    'use strict';

    angular.module( APP_ID )
        .factory( 'MetaService', [
        '$interpolate',
        'ContentSwitchingService',
        'TEXTS',
        MetaService,
    ]);


    /**
     * Manages application's HTML <meta> information.
     *
     * @param {ng.IInterpolateService} $interpolate
     * @param {ContentSwitchingService} ContentSwitchingService
     * @param {Object} TEXTS
     *
     * @returns {MetaService}
     */
    function MetaService(
        $interpolate,
        ContentSwitchingService,
        TEXTS
    ){
        /** @type {MetaService} */
        var $this = this;





        /* ===== PRIVATE MEMBERS ============================================ */


        /** @const */

        /**
         * Format: {subpage} | {page}: {suffix} ({international}) | {site}
         * Example: About | Degree: Subdegree (international students) | University
         */
        var DEFAULT_TITLE_TEMPLATE = "{{ titleStructure.subpage | append: ' | ' }}{{ titleStructure.page }}{{ titleStructure.suffix | prepend: ': ' }}{{ ContentSwitchingService.isInternational() && titleStructure.page ? ' (international students)' : '' }}{{ titleStructure.page ? ' | ' : '' }}{{ titleStructure.site }}",

        /**
         * Format:  {site} | {page} {international} - {subpage}: {suffix}
         * Example: University | Degree - About: Accounting
         */
        DEFAULT_DESCRIPTION_TEMPLATE = "{{ descriptionStructure.subpage }}{{ descriptionStructure.page | prepend:' ' }}{{ descriptionStructure.site | prepend:' ' }}{{ ContentSwitchingService.isInternational() ? ', New Zealand' : '' }}{{ descriptionStructure.suffix ? ( '. ' + descriptionStructure.suffix ) : '' }}";


        /** @type {DOMElement} */
        var metaNoIndexElement = angular.element( '<meta name="robots" content="NOINDEX">' ),
        metaPrerenderStatusElement, metaCanonicalUrl;


        // Members

        var titleTemplate, descriptionTemplate;





        /* ===== EXPOSE TO THE SERVICE ====================================== */


        $this.ContentSwitchingService = ContentSwitchingService;



        /** @type {MetaService} */
        var service = {
            // Set members to defaults
            title:            undefined,
            description:      undefined,
            keywords:         undefined,

            searchAttributes: [],

            // Methods
            setTitleToDefault:          initDefaultTitle,
            updateTitleStructure:       updateTitleStructure,

            setDescriptionTemplate:     setDescriptionTemplate,
            setTitleTemplate:           setTitleTemplate,

            setDescriptionToDefault:    initDefaultDescription,
            updateDescriptionStructure: updateDescriptionStructure,

            resetToDefaults:            resetToDefaults,

            disableIndexing:            disableIndexing,
            addPrerenderHttpStatus:     addPrerenderHttpStatus,
            addCanonicalUrl:            addCanonicalUrl
        };





        /* ===== RUN ON INSTANTIATION ======================================= */


        /** @constructor */
        (function init() {
            initDefaultTitle();
            initDefaultDescription();
        })();



        ////////////////////////////////////////////////////////////////////////

        return service; // Expose API

        ////////////////////////////////////////////////////////////////////////





        /* ===== PUBLIC METHODS ============================================= */


        function initDefaultTitle() {
            setTitleTemplate( DEFAULT_TITLE_TEMPLATE );
            $this.titleStructure = {
                site:       TEXTS.TITLE.DEFAULT,
                page:       null,
                subpage:    null,
                suffix:     null
            };
        }


        function initDefaultDescription() {
            setDescriptionTemplate( DEFAULT_DESCRIPTION_TEMPLATE );
            $this.descriptionStructure = {
                site:       TEXTS.DESCRIPTION.DEFAULT,
                page:       null,
                subpage:    null,
                suffix:     null
            }
        }


        function setTitleTemplate( newTitleTemplate ) {
            titleTemplate = newTitleTemplate;
        }
        function setDescriptionTemplate( newDescriptionTemplate ) {
            descriptionTemplate = newDescriptionTemplate;
        }


        /**
         * Adds <meta> tag to the header to prevent indexing.
         */
        function disableIndexing() {
            angular.element( 'head' ).append( metaNoIndexElement );
        }


        /**
         * Adds <meta> information about the correct HTTP code for prerender.io
         * service. See: https://prerender.io/documentation/best-practices
         *
         * @param {number} statusCode HTTP status code (4xx or 5xx class)
         */
        function addPrerenderHttpStatus( statusCode ) {
            metaPrerenderStatusElement = angular.element( '<meta name="prerender-status-code" content="' + statusCode + '">' );
            angular.element( 'head' ).append( metaPrerenderStatusElement );
        }

        function addCanonicalUrl ( pagePath ) {
            metaCanonicalUrl = angular.element( '<link rel="canonical" href="' + pagePath + '">' );
            angular.element( 'head' ).append( metaCanonicalUrl );
        }


        /**
         * @param {TitleStructure} titleStructureObject
         */
        function updateTitleStructure( titleStructureObject ) {
            angular.merge( $this.titleStructure, titleStructureObject );
            generateTitle();
        }

        /**
         * @param {DescriptionStructure} descriptionStructureObject
         */
        function updateDescriptionStructure( descriptionStructureObject ) {
            angular.merge( $this.descriptionStructure, descriptionStructureObject );
            generateDescription();
        }


        function resetToDefaults(){
            initDefaultTitle();
            initDefaultDescription();
            service.keywords = TEXTS.KEYWORDS.DEFAULT;

            service.searchAttributes = [];

            generateTitle();
            generateDescription();

            removeTemporaryMetaElements();
        }


        function removeTemporaryMetaElements() {
            if ( metaPrerenderStatusElement ) {
                metaPrerenderStatusElement.detach();
                metaPrerenderStatusElement = null;
            }
            if ( metaNoIndexElement ) {
                metaNoIndexElement.detach();
                metaNoIndexElement = null;
            }
        }



        /* ===== PRIVATE FUNCTIONS ========================================== */


        /**
         * Assembles meta title from available template.
         */
        function generateTitle() {
            service.title = $interpolate( titleTemplate )( $this );
        }


        /**
         * Assembles meta description from available template.
         */
        function generateDescription() {
            service.description = $interpolate( descriptionTemplate )( $this );
        }
    }
})();




